import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Award, Calendar, ArrowRight, TreePine, Heart, Users, 
  ExternalLink, FileText, Download, Mail, 
  MapPin, Megaphone, BookOpen, Clock,
  PenTool, Camera, Map, AlertCircle, Globe, Leaf,
  Newspaper, Briefcase
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import {
  CENTENARY_EVENTS_FALLBACK,
  fetchAllEvents,
  getCentenaryEventsFromList,
} from '../utils/eventsSource';
import {
  NATIONAL_NEWS_FALLBACK,
  formatNetworkNewsDate,
  sortNewsByDateDesc,
  resolveNewsImageUrl,
} from '../utils/newsArticle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== TAKE ACTION DATA ==========
const takeActionItems = [
  {
    id: 1,
    title: 'Sign Rural Devolution Letter',
    description: 'Add your name to our letter calling for meaningful rural devolution.',
    icon: PenTool,
    link: 'https://www.cpre.org.uk/get-involved/',
  },
  {
    id: 2,
    title: 'Public Affairs Survey',
    description: 'Help us understand your priorities for countryside protection policy.',
    icon: Megaphone,
    link: 'https://www.cpre.org.uk/get-involved/',
  },
  {
    id: 3,
    title: 'Share Your Countryside Vision',
    description: 'Tell us what the countryside means to you and what you want for its future.',
    icon: Globe,
    link: 'https://www.cpre.org.uk/get-involved/',
  },
  {
    id: 4,
    title: 'NPPF Survey',
    description: 'Respond to the National Planning Policy Framework consultation.',
    icon: FileText,
    link: 'https://www.cpre.org.uk/get-involved/',
  },
];

// ========== OPPORTUNITIES DATA ==========
const opportunities = [
  {
    id: 1,
    title: 'Trustee',
    description: 'Join our Board of Trustees and help guide the future of countryside protection in Rutland and Lincolnshire.',
    icon: Users,
    link: 'mailto:chair@cprerutlandandlincs.uk?subject=Trustee Vacancy Enquiry',
  },
  {
    id: 2,
    title: 'Treasurer',
    description: 'Oversee financial management and reporting for the charity.',
    icon: Briefcase,
    link: 'mailto:chair@cprerutlandandlincs.uk?subject=Treasurer Vacancy Enquiry',
  },
  {
    id: 3,
    title: 'Planning Lead',
    description: 'Lead planning policy responses and development monitoring across the region.',
    icon: MapPin,
    link: 'mailto:chair@cprerutlandandlincs.uk?subject=Planning Lead Enquiry',
  },
];

// ========== STORY TYPES ==========
const storyTypes = [
  { icon: AlertCircle, title: 'Places at Risk', description: 'Countryside under pressure from development' },
  { icon: Leaf, title: 'Restored Countryside', description: 'Success stories of landscape recovery' },
  { icon: Users, title: 'Community Spaces', description: 'Green spaces that bring people together' },
  { icon: TreePine, title: 'Positive Change Projects', description: 'Initiatives making a real difference' },
];

// ========== MAIN CENTENARY PAGE ==========
const CentenaryPage = () => {
  const [centenaryEvents, setCentenaryEvents] = useState(CENTENARY_EVENTS_FALLBACK);
  const [networkNews, setNetworkNews] = useState(NATIONAL_NEWS_FALLBACK);

  useEffect(() => {
    const loadCentenaryEvents = async () => {
      try {
        const allEvents = await fetchAllEvents();
        const cards = getCentenaryEventsFromList(allEvents);
        if (cards.length > 0) {
          setCentenaryEvents(cards);
        }
      } catch (error) {
        console.error('Failed to fetch centenary events:', error);
      }
    };
    loadCentenaryEvents();
  }, []);

  useEffect(() => {
    const loadNetworkNews = async () => {
      try {
        const response = await axios.get(`${API}/news`, {
          params: { source: 'national', limit: 10 },
        });
        const articles = sortNewsByDateDesc(response.data || []);
        if (articles.length > 0) {
          setNetworkNews(articles);
        }
      } catch (error) {
        console.error('Failed to fetch network news:', error);
        setNetworkNews(NATIONAL_NEWS_FALLBACK);
      }
    };
    loadNetworkNews();
  }, []);

  return (
    <Layout>
      <div data-testid="centenary-page">
        {/* 1. Hero Section */}
        <section className="relative py-24 md:py-32 overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600" 
              alt="English countryside" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cpre-new-yew/90 via-cpre-new-yew/70 to-cpre-new-yew/50" />
          </div>
          <div className="relative container-custom text-center">
            <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-6 text-sm px-4 py-2">
              <Award className="w-4 h-4 mr-2 inline" />
              1926 — 2026
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              CPRE: 100 Years of<br />
              <span className="text-cpre-harvest-gold">Countryside Protection</span>
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Celebrating a century of protecting and enhancing the countryside
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/donate">
                <Button size="lg" className="bg-cpre-harvest-gold hover:bg-cpre-harvest-gold/90 text-cpre-new-yew rounded-full px-8">
                  <Heart className="w-5 h-5 mr-2" />
                  Support Our Work
                </Button>
              </Link>
              <a href="https://www.cpre.org.uk/about-us/cpre100/" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cpre-new-yew rounded-full px-8">
                  National Centenary
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* 2. History Slideshow (Embed) */}
        <section className="section-padding bg-cpre-heritage-white" data-testid="history-slideshow">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                CPRE History Presentation
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Explore 100 years of countryside protection through our history presentation.
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-stone-200 bg-white" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src="https://cpretree.sharepoint.com/:p:/s/CPRENetwork/IQD5KTWsSDeWQYzcn53Ah6PEAdVpBnzW5N9w1PvV8SPd6lg?e=5BZsK4&action=embedview"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  title="CPRE History Presentation"
                />
              </div>
            </div>
          </div>
        </section>


        {/* 4. Current Campaigns - On the Edge */}
        <section className="section-padding bg-cpre-new-yew" data-testid="on-the-edge-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-4">Centenary Campaign</Badge>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Current Campaigns</h2>
            </div>

            <a 
              href="https://www.cpre.org.uk/news/gold-medal-and-garden-of-the-year-for-cpre-garden-at-rhs-chelsea/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block max-w-4xl mx-auto group"
              data-testid="on-the-edge-card"
            >
              <Card className="bg-white border-0 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="aspect-video md:aspect-auto overflow-hidden">
                    <img 
                      src="/images/cpre images for website/30.jpg" 
                      alt="Countryside on the edge of towns"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white w-fit mb-4">On the Edge</Badge>
                    <h3 className="font-heading text-2xl font-bold text-cpre-new-yew mb-4">
                      On the Edge Campaign
                    </h3>
                    <p className="text-muted-foreground mb-4">
                    🏅 On the Edge garden wins Gold Medal and Garden of the Year at RHS Chelsea 2026. Over 9,000 campaign signatures so far.
                    </p>
                    <Button className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-full w-fit">
                      Learn More
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </Card>
            </a>
          </div>
        </section>

        {/* 5. Stories Section (Placeholder) */}
        <section className="section-padding" data-testid="stories-section">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                Stories from the Countryside
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                This section will showcase places at risk, restored countryside, community spaces, and positive change projects.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {storyTypes.map((type, index) => (
                <Card key={index} className="bg-white border border-stone-200 hover:border-cpre-new-yew hover:shadow-lg transition-all text-center">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-cpre-pebble/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <type.icon className="w-7 h-7 text-cpre-new-yew" />
                    </div>
                    <h4 className="font-heading font-semibold text-cpre-new-yew mb-2">{type.title}</h4>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Events Section (Display only) */}
        <section className="section-padding bg-cpre-heritage-white" data-testid="events-section">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew">Events</h2>
              </div>
              <Link to="/events">
                <Button variant="outline" className="mt-4 md:mt-0 rounded-full border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white">
                  View All Events
                  <Calendar className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {centenaryEvents.map((event) => (
                <Card key={event.id} className="bg-white border border-stone-200 hover:shadow-lg transition-shadow h-full overflow-hidden">
                  <CardContent className="p-0 h-full">
                    <div className="flex h-full items-stretch">
                      <div className="w-20 bg-cpre-new-yew flex flex-col items-center justify-center text-white flex-shrink-0 self-stretch">
                        {event.day && (
                          <>
                            <span className="text-2xl font-bold">{event.day}</span>
                            <span className="text-xs uppercase">{event.month}</span>
                          </>
                        )}
                        {!event.day && (
                          <Calendar className="w-6 h-6" />
                        )}
                      </div>
                      <div className="flex-1 p-5">
                        <Badge className="bg-cpre-pebble text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white text-xs mb-2">{event.type}</Badge>
                        <h4 className="font-heading font-semibold text-cpre-new-yew mb-2">{event.title}</h4>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{event.time}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Opportunities / Get Involved */}
        <section id="centenary-projects" className="section-padding" data-testid="opportunities-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                Get Involved
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                We're looking for passionate people to help protect the countryside.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {opportunities.map((opp) => (
                <Card key={opp.id} className="bg-white border border-stone-200 hover:shadow-lg hover:border-cpre-new-yew transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-cpre-new-yew rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <opp.icon className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="font-heading font-semibold text-lg text-cpre-new-yew mb-3">{opp.title}</h4>
                    <p className="text-sm text-muted-foreground mb-4">{opp.description}</p>
                    <a href={opp.link}>
                      <Button size="sm" className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-full">
                        Contact Us
                        <Mail className="w-3 h-3 ml-2" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 8. Take Action */}
        <section className="section-padding bg-cpre-autumn-leaf" data-testid="take-action-section">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Take Action</h2>
              <p className="text-white/80 max-w-xl mx-auto">Your voice matters. Take action now to protect our countryside.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {takeActionItems.map((item) => (
                <a 
                  key={item.id} 
                  href={item.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group"
                >
                  <Card className="bg-white border-0 shadow-lg h-full group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-cpre-autumn-leaf/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-6 h-6 text-cpre-autumn-leaf" />
                      </div>
                      <h4 className="font-heading font-semibold text-cpre-new-yew mb-2">{item.title}</h4>
                      <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                      <span className="text-sm font-medium text-cpre-autumn-leaf flex items-center justify-center gap-1">
                        Take Action <ExternalLink className="w-3 h-3" />
                      </span>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* 9. Campaign Map (Coming Soon) */}
        <section className="section-padding bg-cpre-pebble/30" data-testid="campaign-map-section">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <Badge className="bg-cpre-bumblebee text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white mb-4">Coming Soon</Badge>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
                On the Edge Map
              </h2>
              <p className="text-muted-foreground mb-8">
                Interactive campaign map coming soon.
              </p>

              <div className="bg-white rounded-2xl p-10 border border-stone-200">
                <div className="w-20 h-20 bg-cpre-pebble rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Map className="w-10 h-10 text-cpre-new-yew" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-cpre-new-yew mb-3">Coming Soon</h3>
                <p className="text-sm text-muted-foreground">
                  An interactive map to explore countryside stories from across Rutland and Lincolnshire.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 10. Network News */}
        <section className="section-padding" data-testid="network-news-section">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew">
                Latest Network News
              </h2>
              <a href="https://www.cpre.org.uk/news/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="mt-4 md:mt-0 rounded-full border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white">
                  CPRE National News
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {networkNews.map((item) => (
                <a 
                  key={item.id}
                  href={item.external_url || 'https://www.cpre.org.uk/news/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <Card className="bg-white border border-stone-200 h-full overflow-hidden hover:shadow-lg hover:border-cpre-new-yew transition-all duration-300">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={resolveNewsImageUrl(item.image_url)}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-cpre-pebble/50 rounded-xl flex items-center justify-center mb-4">
                        <Newspaper className="w-6 h-6 text-cpre-new-yew" />
                      </div>
                      <span className="text-xs text-muted-foreground">{formatNetworkNewsDate(item.created_at)}</span>
                      <h4 className="font-heading font-semibold text-cpre-new-yew mt-1 mb-3 group-hover:text-cpre-hill transition-colors">
                        {item.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">{item.excerpt}</p>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-cpre-harvest-gold">
          <div className="container-custom text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-cpre-new-yew mb-4">
              Join Us in Protecting the Countryside
            </h2>
            <p className="text-cpre-new-yew/80 max-w-2xl mx-auto mb-8">
              Whether you volunteer, donate, or simply spread the word, you can help ensure 
              another 100 years of countryside protection.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/volunteering">
                <Button size="lg" className="bg-cpre-new-yew hover:bg-cpre-new-yew/90 text-white rounded-full px-8">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer
                </Button>
              </Link>
              <Link to="/donate">
                <Button size="lg" variant="outline" className="border-2 border-cpre-new-yew text-cpre-new-yew hover:bg-cpre-new-yew hover:text-white rounded-full px-8">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CentenaryPage;
